import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getRefundDetails, clearRefund } from '../../actions/refund';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Alert, 
    Spinner, 
    Badge
} from 'react-bootstrap';
import { 
    ArrowLeft,
    IndianRupee,
    Calendar,
    CheckCircle,
    Clock,
    XCircle,
    FileText,
    CreditCard,
    Receipt
} from 'lucide-react';

const Refund = ({ 
    getRefundDetails, 
    clearRefund,
    refund, 
    loading, 
    error 
}) => {
    const { id } = useParams();

    useEffect(() => {
        document.title = 'Refund Details';
        if (id) {
            getRefundDetails(id);
        }
        return () => {
            clearRefund();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            processed: { variant: 'success', icon: <CheckCircle size={20} className="me-2" /> },
            pending: { variant: 'warning', icon: <Clock size={20} className="me-2" /> },
            failed: { variant: 'danger', icon: <XCircle size={20} className="me-2" /> }
        };
        const config = statusConfig[status?.toLowerCase()] || { variant: 'secondary', icon: null };
        return (
            <Badge bg={config.variant} className="fs-6 p-2 d-flex align-items-center" style={{ width: 'fit-content' }}>
                {config.icon}
                {status || 'Unknown'}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading refund details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid py-4">
                <Alert variant="danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
                <Link to="/inventory/v1.2/refunds" className="btn btn-primary mt-3">
                    <ArrowLeft size={16} className="me-2" />
                    Back to Refunds
                </Link>
            </div>
        );
    }

    if (!refund) {
        return (
            <div className="container-fluid py-4">
                <Alert variant="info">
                    <i className="fas fa-info-circle me-2"></i>
                    Refund not found
                </Alert>
                <Link to="/inventory/v1.2/refunds" className="btn btn-primary mt-3">
                    <ArrowLeft size={16} className="me-2" />
                    Back to Refunds
                </Link>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <Link 
                        to="/inventory/v1.2/refunds"
                        className="btn btn-outline-primary mb-3"
                    >
                        <ArrowLeft size={16} className="me-2" />
                        Back to Refunds
                    </Link>
                    <div>
                        <h2 className="text-primary mb-1">
                            <IndianRupee size={32} className="me-3" />
                            Refund Details
                        </h2>
                        <p className="text-muted mb-0">
                            Detailed information about refund {refund.refundId}
                        </p>
                    </div>
                </div>

                <Row>
                    {/* Main Details */}
                    <Col lg={8}>
                        <Card className="shadow-sm mb-4">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-info-circle me-2"></i>
                                    Refund Information
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <FileText size={20} className="me-3 text-primary" />
                                            <div>
                                                <small className="text-muted d-block">Refund ID</small>
                                                <div className="fw-bold">{refund.refundId}</div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <CreditCard size={20} className="me-3 text-info" />
                                            <div>
                                                <small className="text-muted d-block">Payment ID</small>
                                                <code className="fw-bold">{refund.paymentId}</code>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <IndianRupee size={20} className="me-3 text-success" />
                                            <div>
                                                <small className="text-muted d-block">Refund Amount</small>
                                                <div className="fw-bold text-success fs-5">
                                                    ₹{refund.amount?.toLocaleString() || '0.00'}
                                                </div>
                                                <small className="text-muted">{refund.currency || 'INR'}</small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <CheckCircle size={20} className="me-3 text-primary" />
                                            <div>
                                                <small className="text-muted d-block">Status</small>
                                                {getStatusBadge(refund.status)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <Clock size={20} className="me-3 text-warning" />
                                            <div>
                                                <small className="text-muted d-block">Speed</small>
                                                <Badge bg="info" className="fs-6">
                                                    {refund.speed || 'normal'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            <Receipt size={20} className="me-3 text-secondary" />
                                            <div>
                                                <small className="text-muted d-block">Receipt</small>
                                                {refund.receipt ? (
                                                    <code>{refund.receipt}</code>
                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <div className="d-flex align-items-start">
                                            <Calendar size={20} className="me-3 text-muted mt-1" />
                                            <div>
                                                <small className="text-muted d-block">Created At</small>
                                                <div className="fw-bold">
                                                    {formatDate(refund.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Notes */}
                        {refund.notes && (
                            <Card className="shadow-sm mb-4">
                                <Card.Header className="bg-info text-white">
                                    <h5 className="mb-0">
                                        <i className="fas fa-sticky-note me-2"></i>
                                        Notes
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    {typeof refund.notes === 'string' ? (
                                        <p className="mb-0">{refund.notes}</p>
                                    ) : (
                                        <pre className="mb-0" style={{whiteSpace: 'pre-wrap'}}>
                                            {JSON.stringify(refund.notes, null, 2)}
                                        </pre>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Acquirer Data */}
                        {refund.acquirer_data && (
                            <Card className="shadow-sm">
                                <Card.Header className="bg-secondary text-white">
                                    <h5 className="mb-0">
                                        <i className="fas fa-database me-2"></i>
                                        Acquirer Data
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <pre style={{whiteSpace: 'pre-wrap'}}>
                                        {JSON.stringify(refund.acquirer_data, null, 2)}
                                    </pre>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>

                    {/* Summary Card */}
                    <Col lg={4}>
                        <Card className="shadow-sm">
                            <Card.Header className="bg-success text-white">
                                <h5 className="mb-0">
                                    <i className="fas fa-summary me-2"></i>
                                    Summary
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Refund ID</small>
                                    <div className="fw-bold text-break">{refund.refundId}</div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Payment ID</small>
                                    <code className="text-break">{refund.paymentId}</code>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Amount</small>
                                    <div className="fw-bold text-success fs-4">
                                        ₹{refund.amount?.toLocaleString() || '0.00'}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Status</small>
                                    {getStatusBadge(refund.status)}
                                </div>
                                <div className="mb-3">
                                    <small className="text-muted d-block">Speed</small>
                                    <Badge bg="info">{refund.speed || 'normal'}</Badge>
                                </div>
                                <div className="mb-0">
                                    <small className="text-muted d-block">Created</small>
                                    <div className="fw-bold">
                                        {formatDate(refund.createdAt)}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

Refund.propTypes = {
    getRefundDetails: PropTypes.func.isRequired,
    clearRefund: PropTypes.func.isRequired,
    refund: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string
};

const mapStateToProps = state => ({
    refund: state.refund.refund,
    loading: state.refund.loading,
    error: state.refund.error
});

export default connect(mapStateToProps, { getRefundDetails, clearRefund })(Refund);

